// Stick Stories — Cart store (localStorage-backed)
// Single source of truth for cart items across pages. Lightweight: we
// keep only what's needed to render the cart line (label, summary,
// price, qty, a 220px cover thumbnail) — we don't dump every raw photo
// into localStorage.

const SS_CART_KEY = 'stickStoriesCart_v1';
const SS_CART_EVT = 'ss-cart-changed';

function ssReadCart() {
  try {
    const raw = localStorage.getItem(SS_CART_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

function ssWriteCart(items) {
  try {
    localStorage.setItem(SS_CART_KEY, JSON.stringify(items));
  } catch (e) {
    // Quota — drop oldest thumbnails and retry once.
    try {
      const trimmed = items.map((it) => ({ ...it, coverThumb: null }));
      localStorage.setItem(SS_CART_KEY, JSON.stringify(trimmed));
    } catch (e2) {/* give up silently */}
  }
  try { window.dispatchEvent(new Event(SS_CART_EVT)); } catch (e) {}
}

function ssCartCount() {
  return ssReadCart().reduce((n, it) => n + (it.qty || 1), 0);
}

function ssAddCartItem(item) {
  const items = ssReadCart();
  const id = 'it_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7);
  items.push({ ...item, id, qty: item.qty || 1, addedAt: Date.now() });
  ssWriteCart(items);
  return id;
}

function ssUpdateCartItem(id, patch) {
  const items = ssReadCart().map((it) => it.id === id ? { ...it, ...patch } : it);
  ssWriteCart(items);
}

function ssRemoveCartItem(id) {
  ssWriteCart(ssReadCart().filter((it) => it.id !== id));
}

function ssClearCart() {
  ssWriteCart([]);
}

// Downsample a data-URL image so we can safely persist a small cover
// thumbnail in localStorage. Returns a Promise resolving to a JPEG dataURL.
function ssShrinkImage(dataUrl, maxSide = 240, quality = 0.78) {
  return new Promise((resolve) => {
    if (!dataUrl) return resolve(null);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      try { resolve(c.toDataURL('image/jpeg', quality)); }
      catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

// React hook: subscribes a component to cart changes (storage events
// from other tabs + our in-tab custom event). Returns { items, count }.
function useSsCart() {
  const [items, setItems] = React.useState(() => ssReadCart());
  React.useEffect(() => {
    const sync = () => setItems(ssReadCart());
    window.addEventListener(SS_CART_EVT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SS_CART_EVT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const count = items.reduce((n, it) => n + (it.qty || 1), 0);
  return { items, count, setItems };
}

Object.assign(window, {
  ssReadCart, ssWriteCart, ssCartCount,
  ssAddCartItem, ssUpdateCartItem, ssRemoveCartItem, ssClearCart,
  ssShrinkImage, useSsCart,
  SS_CART_KEY, SS_CART_EVT
});
